import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ConsequenceAuditByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetConsequenceAuditByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcConsequenceAuditToGraphQL(
  trpcData: ConsequenceAuditByIdResponseRow[]
): GetConsequenceAuditByIdQuery {
  return {
    consequence_audit: trpcData ?? [],
  };
}

export const useGetConsequenceAuditByIdTRPC = (id: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.consequence.getConsequenceAuditById.queryOptions({
      id,
    }),

    enabled: trpcEnabled && !skip, // Only enable when TRPC flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcConsequenceAuditToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
